import { getApiClient, type RequestOpts } from './api-client';
import { SEARCH_SYNONYMS_SERVICE_URL as servicePath } from '../constants/APIconstants';

export type MappingType = 'equivalent' | 'explicit';

export interface ActorSnapshot {
  userId: string;
  email: string;
}

export interface SynonymRecord {
  id: string;
  mappingType: MappingType;
  synonyms: string[];
  input: string[];
  note?: string;
  createdBy?: ActorSnapshot;
  updatedBy?: ActorSnapshot;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSynonymPayload {
  mappingType: MappingType;
  synonyms: string[];
  input?: string[];
  note?: string;
}

export type UpdateSynonymPayload = Partial<CreateSynonymPayload>;

export interface GetSynonymsParams extends RequestOpts {
  mappingType?: MappingType;
}

export interface UpdateSynonymArgs {
  id: string;
  payload: UpdateSynonymPayload;
}

// The endpoint wraps results in `{ synonyms }` / `{ synonym }` (unlike rotations,
// which returns the entity directly). Unwrap here so hooks/consumers get clean records.
interface SynonymsListResponse {
  synonyms: SynonymRecord[];
}
interface SynonymResponse {
  synonym: SynonymRecord;
}

/**
 * Search-synonyms API (Atlas Search synonym mappings). Global, ROOT-only — the
 * backend returns 403 for non-ROOT callers. Methods throw on failure (axios
 * default), return data directly, and accept `{ signal }` for cancellation.
 *
 * Consumers should reach this through the hooks in
 * `shared/hooks/search-synonyms.hooks`; direct use is only appropriate inside
 * `queryFn` / `mutationFn` bodies.
 */
class SearchSynonymsService {
  async getSynonyms({ mappingType, ...opts }: GetSynonymsParams = {}): Promise<SynonymRecord[]> {
    const query = mappingType ? `?mappingType=${mappingType}` : '';
    const res = await getApiClient().get<SynonymsListResponse>(`${servicePath}${query}`, opts);
    return res.synonyms;
  }

  async getSynonymById(id: string, opts?: RequestOpts): Promise<SynonymRecord> {
    const res = await getApiClient().get<SynonymResponse>(`${servicePath}${id}`, opts);
    return res.synonym;
  }

  async createSynonym(payload: CreateSynonymPayload): Promise<SynonymRecord> {
    const res = await getApiClient().post<SynonymResponse>(servicePath, payload);
    return res.synonym;
  }

  async updateSynonym({ id, payload }: UpdateSynonymArgs): Promise<SynonymRecord> {
    const res = await getApiClient().put<SynonymResponse>(`${servicePath}${id}`, payload);
    return res.synonym;
  }

  deleteSynonym({ id }: { id: string }): Promise<unknown> {
    return getApiClient().delete(`${servicePath}${id}`);
  }
}

export default new SearchSynonymsService();
